import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaMapMarkerAlt, FaEnvelope, FaPhone } from "react-icons/fa";
import {
  submitContactForm,
  clearFormState,
} from "../store/slices/contactSlice";

const Contact = () => {
  const dispatch = useDispatch();
  const { formSubmitting, formSuccess, formError } = useSelector(
    (state) => state.contact,
  );

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: "",
    email: "",
    subject: "",
    message: "",
  });

  const EMPTY_FORM = {
    name: "",
    phone: "",
    city: "",
    email: "",
    subject: "",
    message: "",
  };

  useEffect(() => {
    if (formSuccess) {
      toast.success(
        "Your enquiry has been submitted successfully! We'll get back to you shortly.",
        {
          position: "top-right",
          autoClose: 5000,
        },
      );
      setFormData(EMPTY_FORM);
      dispatch(clearFormState());
    }
  }, [formSuccess]);

  useEffect(() => {
    if (formError) {
      toast.error(formError || "Something went wrong. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
      dispatch(clearFormState());
    }
  }, [formError]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.warning("Please enter your name.");
      return;
    }
    if (!formData.email.trim()) {
      toast.warning("Please enter your email address.");
      return;
    }
    if (!formData.subject) {
      toast.warning("Please select a purpose.");
      return;
    }
    if (!formData.message.trim()) {
      toast.warning("Please enter your message.");
      return;
    }
    const fd = new FormData();
    Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
    dispatch(submitContactForm(fd));
  };

  const infoCards = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Office Address",
      detail: "Peenya 2nd Stage, Bangalore - 560058",
    },
    {
      icon: <FaEnvelope />,
      title: "Email Address",
      detail: "info@ommaruticreation.com",
    },
    {
      icon: <FaPhone />,
      title: "Contact No",
      detail: "+91 0987654321",
    },
  ];

  return (
    <>
      {/* ── Page Hero ── */}
      <section className="relative h-[480px] overflow-hidden">
        <img
          src="/images/banner.png"
          alt="Contact Us"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <span className="inline-block bg-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest text-white mb-5">
              Om Maruticreation
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Contact Us
            </h1>
          </div>
        </div>
      </section>

      {/* ── Contact Info Cards ── */}
      <section className="py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* 3 info cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {infoCards.map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center hover:shadow-lg transition-shadow duration-300"
              >
                <div className="h-14 w-14 bg-blue-700 text-white rounded-full flex items-center justify-center text-xl mx-auto mb-4">
                  {card.icon}
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{card.title}</h4>
                <p className="text-gray-500 text-sm">{card.detail}</p>
              </div>
            ))}
          </div>

          {/* 2-col: form + map */}
          <div className="grid lg:grid-cols-1 gap-12 items-start">
            {/* Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Send Us a Message
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Fill out the form below and our team will get back to you
                shortly.
              </p>

              {formSuccess && (
                <div className="mb-5 p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 text-sm">
                  Your enquiry has been submitted successfully!
                </div>
              )}
              {formError && (
                <div className="mb-5 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 text-sm">
                  {formError}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                {/* Row 1: name + phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Mobile No"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                {/* Row 2: city + email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                {/* Subject dropdown */}
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white text-gray-700"
                >
                  <option value="" disabled>
                    Select Purpose
                  </option>
                  <option value="Bulk Order">Bulk Order</option>
                  <option value="General Enquiry">General Enquiry</option>
                  <option value="Franchise Enquiry">Franchise Enquiry</option>
                  <option value="Support">Support</option>
                  <option value="Other">Other</option>
                </select>

                {/* Message */}
                <textarea
                  rows="5"
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-xl px-5 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                />

                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3.5 rounded-full transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60"
                >
                  {formSubmitting ? "Submitting…" : "Get Enquiry Now"}
                </button>
              </form>
            </div>

            {/* Map — commented out
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Find Us Here
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                Visit our manufacturing facility at Peenya 2nd Stage, Bangalore.
              </p>
              <iframe
                className="w-full h-[480px] rounded-2xl border border-gray-100 shadow-sm"
                src="https://maps.google.com/maps?q=Peenya%202nd%20Stage%20Bangalore&t=&z=13&ie=UTF8&iwloc=&output=embed"
                allowFullScreen=""
                loading="lazy"
                title="OM Maruti Creation Location"
              />
            </div>
            */}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
